import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardActionArea, CardMedia, CardContent, Grid, Chip, Stack,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Alert, Paper, Divider, Tooltip, LinearProgress, InputAdornment, Avatar, AvatarGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useDropzone } from 'react-dropzone';
import { looksAPI, wardrobeAPI } from '../services/api';
import { useUI } from '../contexts/UIContext';

interface LookItem {
  id: number;
  name: string;
  category: string;
  subcategory?: string;
  brand?: string;
  colors?: string[];
  image_url?: string;
  score?: number;
  category_match?: boolean;
}

interface Piece {
  label: string;
  category?: string;
  subcategory?: string;
  colors?: string[];
  description?: string;
  candidates: LookItem[];
}

interface LookImage {
  id: string;
  url: string;
  s3_key?: string;
  pieces: Piece[];
}

interface LookOutfit {
  id: number;
  name: string;
  items: LookItem[];
}

interface LookSummary {
  id: number;
  name: string;
  description?: string;
  occasion?: string;
  season?: string;
  tags: string[];
  cover_url?: string;
  image_count: number;
  item_count: number;
  outfit_count: number;
}

interface LookDetail extends LookSummary {
  notes?: string;
  source?: string;
  images: LookImage[];
  item_ids: number[];
  items: LookItem[];
  outfits: LookOutfit[];
}

interface LookForm {
  name: string;
  description: string;
  notes: string;
  source: string;
  occasion: string;
  season: string;
  tags: string;
}

const emptyForm: LookForm = { name: '', description: '', notes: '', source: '', occasion: '', season: '', tags: '' };

const formToPayload = (f: LookForm) => ({
  name: f.name.trim(),
  description: f.description.trim() || null,
  notes: f.notes.trim() || null,
  source: f.source.trim() || null,
  occasion: f.occasion.trim() || null,
  season: f.season.trim() || null,
  tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
});

const errorText = (err: any, fallback: string) => err?.response?.data?.error || err?.response?.data?.description || fallback;

// Upload photos one at a time: each request runs vision + matching for one photo.
async function uploadPhotos(
  lookId: number,
  files: File[],
  onProgress: (done: number) => void,
): Promise<{ look: LookDetail | null; added: number; failed: number }> {
  let look: LookDetail | null = null;
  let added = 0;
  let failed = 0;
  for (let i = 0; i < files.length; i++) {
    try {
      const res = await looksAPI.addImage(lookId, files[i]);
      look = res.data;
      added += (res.data.added_item_ids || []).length;
    } catch {
      failed += 1;
    }
    onProgress(i + 1);
  }
  return { look, added, failed };
}

const IMAGE_ACCEPT = { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] };

// Drag-and-drop target that also opens the file browser on click (same as Upload).
const PhotoDropzone: React.FC<{
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  compact?: boolean;
  label?: string;
}> = ({ onFiles, disabled, compact, label = 'Drag & drop photos of the look' }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => accepted.length > 0 && onFiles(accepted),
    accept: IMAGE_ACCEPT,
    multiple: true,
    disabled,
  });

  return (
    <Box
      {...getRootProps()}
      data-local-dropzone
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? 'secondary.main' : 'rgba(0,0,0,0.15)',
        backgroundColor: isDragActive ? 'rgba(212, 175, 55, 0.08)' : 'transparent',
        borderRadius: compact ? 1.5 : 2,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': disabled ? undefined : { borderColor: 'primary.main', backgroundColor: 'rgba(0,0,0,0.02)' },
        ...(compact
          ? { width: { xs: 120, md: 150 }, height: { xs: 160, md: 200 }, flex: '0 0 auto', p: 1 }
          : { minHeight: 160, p: 3 }),
      }}
    >
      <input {...getInputProps()} />
      <CloudUploadIcon
        sx={{
          fontSize: compact ? 32 : 48,
          color: isDragActive ? 'secondary.main' : 'rgba(0,0,0,0.35)',
          mb: 1,
          transition: 'color 0.25s ease, transform 0.25s ease',
          transform: isDragActive ? 'translateY(-4px)' : 'none',
        }}
      />
      {compact ? (
        <Typography variant="caption" color="text.secondary">
          {isDragActive ? 'Drop to add' : 'Drop or click to add photos'}
        </Typography>
      ) : (
        <>
          <Typography sx={{ fontWeight: 600 }}>{isDragActive ? 'Drop the photos here' : label}</Typography>
          <Typography variant="body2" color="text.secondary">
            {isDragActive ? '' : 'or click to browse — select one or many'}
          </Typography>
        </>
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Create / edit dialog                                                */
/* ------------------------------------------------------------------ */

const LookFormDialog: React.FC<{
  open: boolean;
  title: string;
  initial: LookForm;
  withPhotos: boolean;
  initialFiles?: File[];
  // An already-saved photo of the look to write details from (edit mode)
  describeS3Key?: string | null;
  busyLabel?: string | null;
  onClose: () => void;
  onSubmit: (form: LookForm, files: File[]) => void;
}> = ({ open, title, initial, withPhotos, initialFiles, describeS3Key, busyLabel, onClose, onSubmit }) => {
  const [form, setForm] = useState<LookForm>(initial);
  const [files, setFiles] = useState<File[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState(false);
  const describedFile = useRef<File | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setFiles(initialFiles || []);
      setSuggested(false);
      describedFile.current = null;
    }
    requestId.current += 1; // drop any in-flight suggestion from a previous opening
    setSuggesting(false);
  }, [open, initial, initialFiles]);

  // Ask vision to write the details. Automatic runs only fill blank fields so
  // nothing the user typed is lost; an explicit "Suggest" click replaces them.
  const suggest = async (source: File | string, overwrite: boolean) => {
    const id = ++requestId.current;
    setSuggesting(true);
    try {
      const res = await looksAPI.describe(source);
      if (id !== requestId.current) return;
      const d = res.data || {};
      const next: Partial<LookForm> = {
        name: d.name || '',
        description: d.description || '',
        occasion: d.occasion || '',
        season: d.season || '',
        tags: (d.tags || []).join(', '),
      };
      setForm((prev) => {
        const merged = { ...prev };
        (Object.keys(next) as (keyof LookForm)[]).forEach((k) => {
          if (next[k] && (overwrite || !prev[k].trim())) merged[k] = next[k] as string;
        });
        return merged;
      });
      setSuggested(true);
    } catch {
      if (id === requestId.current && overwrite) setSuggested(false);
    } finally {
      if (id === requestId.current) setSuggesting(false);
    }
  };

  // Describe the first photo as soon as one is added
  useEffect(() => {
    if (!open || !withPhotos || files.length === 0 || describedFile.current === files[0]) return;
    describedFile.current = files[0];
    suggest(files[0], false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, withPhotos, files]);

  const suggestSource: File | string | null = files[0] || describeS3Key || null;

  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const set = (key: keyof LookForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <Dialog open={open} onClose={busyLabel ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {withPhotos && (
            <Box>
              <PhotoDropzone disabled={!!busyLabel} onFiles={(added) => setFiles((prev) => [...prev, ...added])} />
              {files.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, overflowX: 'auto', pb: 0.5 }}>
                  {previews.map((src, i) => (
                    <Box key={src} sx={{ position: 'relative', flex: '0 0 auto' }}>
                      <Box component="img" src={src} alt="" sx={{ width: 84, height: 112, objectFit: 'cover', borderRadius: 1 }} />
                      {!busyLabel && (
                        <IconButton
                          size="small"
                          onClick={() => setFiles((prev) => prev.filter((_, n) => n !== i))}
                          sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', p: 0.25 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {suggestSource && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 32 }}>
              {suggesting ? (
                <>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Writing details from your photo…</Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {suggested ? 'Details suggested from your photo — edit anything.' : 'Let AI name and describe this look.'}
                  </Typography>
                  <Button size="small" startIcon={<AutoAwesomeIcon />} onClick={() => suggest(suggestSource, true)} disabled={!!busyLabel}>
                    {suggested ? 'Try again' : 'Suggest details'}
                  </Button>
                </>
              )}
            </Stack>
          )}
          <TextField
            label="Name"
            value={form.name}
            onChange={set('name')}
            required
            fullWidth
            autoFocus
            placeholder={suggesting ? 'Suggesting a name…' : undefined}
            InputLabelProps={suggesting ? { shrink: true } : undefined}
          />
          <TextField label="Description" value={form.description} onChange={set('description')} fullWidth multiline minRows={2} />
          <TextField label="Source" placeholder="Link, account, magazine…" value={form.source} onChange={set('source')} fullWidth />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Occasion" value={form.occasion} onChange={set('occasion')} fullWidth />
            <TextField label="Season" value={form.season} onChange={set('season')} fullWidth />
          </Stack>
          <TextField label="Tags" placeholder="Comma separated" value={form.tags} onChange={set('tags')} fullWidth />
          <TextField label="Notes" value={form.notes} onChange={set('notes')} fullWidth multiline minRows={2} />
          {busyLabel && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{busyLabel}</Typography>
              <LinearProgress />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!busyLabel}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form, files)} disabled={!form.name.trim() || !!busyLabel}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Wardrobe item picker (manual additions)                             */
/* ------------------------------------------------------------------ */

const ItemPickerDialog: React.FC<{
  open: boolean;
  selectedIds: number[];
  onClose: () => void;
  onSave: (ids: number[]) => void;
}> = ({ open, selectedIds, onClose, onSave }) => {
  const [items, setItems] = useState<LookItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open) return;
    setPicked(new Set(selectedIds));
    setQuery('');
    setCategory(null);
    setLoading(true);
    wardrobeAPI.getItems()
      .then((res) => {
        const list: any[] = res.data?.items || res.data || [];
        setItems(list.map((i) => ({ ...i, image_url: i.thumbnail_url || i.image_url })));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, selectedIds]);

  const categories = useMemo(() => Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort(), [items]);
  const visible = items.filter((i) => {
    if (category && i.category !== category) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [i.name, i.brand, i.subcategory, i.category, ...(i.colors || [])]
      .some((v) => v && String(v).toLowerCase().includes(q));
  });

  const toggle = (id: number) => setPicked((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Keep the existing order and append newly picked items at the end
  const result = () => [
    ...selectedIds.filter((id) => picked.has(id)),
    ...Array.from(picked).filter((id) => !selectedIds.includes(id)),
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add pieces from your wardrobe</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, brand, color…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" spacing={1} sx={{ mb: 2, overflowX: 'auto', pb: 0.5 }}>
          <Chip label="All" color={category === null ? 'primary' : 'default'} onClick={() => setCategory(null)} />
          {categories.map((c) => (
            <Chip key={c} label={c} color={category === c ? 'primary' : 'default'} onClick={() => setCategory(c)} sx={{ textTransform: 'capitalize' }} />
          ))}
        </Stack>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No matching items.</Typography>
        ) : (
          <Grid container spacing={1.5}>
            {visible.map((item) => {
              const on = picked.has(item.id);
              return (
                <Grid size={{ xs: 4, sm: 3, md: 2 }} key={item.id}>
                  <Card
                    variant="outlined"
                    onClick={() => toggle(item.id)}
                    sx={{ cursor: 'pointer', position: 'relative', outline: '2px solid', outlineColor: on ? 'primary.main' : 'transparent' }}
                  >
                    {on && <CheckCircleIcon color="primary" sx={{ position: 'absolute', top: 4, right: 4, bgcolor: '#fff', borderRadius: '50%' }} />}
                    <CardMedia component="img" image={item.image_url} alt={item.name} sx={{ height: 100, objectFit: 'cover', bgcolor: '#f5f5f5' }} />
                    <Typography variant="caption" noWrap sx={{ display: 'block', px: 0.75, py: 0.5 }}>{item.name}</Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>{picked.size} selected</Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(result())}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

const ItemTile: React.FC<{ item: LookItem; onRemove?: () => void }> = ({ item, onRemove }) => (
  <Card variant="outlined" sx={{ position: 'relative', height: '100%' }}>
    {onRemove && (
      <IconButton
        size="small"
        aria-label={`Remove ${item.name}`}
        onClick={onRemove}
        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', p: 0.25, '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    )}
    <CardMedia component="img" image={item.image_url} alt={item.name} sx={{ height: 140, objectFit: 'cover', objectPosition: 'center top', bgcolor: '#f5f5f5' }} />
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>{item.name}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', textTransform: 'capitalize' }}>
        {item.subcategory || item.category}
      </Typography>
    </CardContent>
  </Card>
);

const SectionHeader: React.FC<{ title: string; subtitle?: string; action?: React.ReactNode }> = ({ title, subtitle, action }) => (
  <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
    </Box>
    {action}
  </Stack>
);

/* ------------------------------------------------------------------ */
/* Look detail                                                         */
/* ------------------------------------------------------------------ */

const LookDetailView: React.FC<{ lookId: number }> = ({ lookId }) => {
  const navigate = useNavigate();
  const { toast, confirm } = useUI();
  const fileRef = useRef<HTMLInputElement>(null);

  const [look, setLook] = useState<LookDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImageId, setActiveImageId] = useState<string | null>(null);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [rematching, setRematching] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [outfitName, setOutfitName] = useState('');
  const [savingOutfit, setSavingOutfit] = useState(false);

  useEffect(() => {
    setLoading(true);
    looksAPI.getLook(lookId)
      .then((res) => setLook(res.data))
      .catch((err) => setError(err?.response?.status === 404 ? 'This look no longer exists.' : 'Failed to load the look.'))
      .finally(() => setLoading(false));
  }, [lookId]);

  useEffect(() => {
    if (!look) return;
    if (!activeImageId || !look.images.some((img) => img.id === activeImageId)) {
      setActiveImageId(look.images[0]?.id || null);
    }
  }, [look, activeImageId]);

  const editInitial = useMemo<LookForm>(() => ({
    name: look?.name || '',
    description: look?.description || '',
    notes: look?.notes || '',
    source: look?.source || '',
    occasion: look?.occasion || '',
    season: look?.season || '',
    tags: (look?.tags || []).join(', '),
  }), [look]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error || !look) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/look-book')} sx={{ mb: 2 }}>Look Book</Button>
        <Alert severity="error">{error || 'Look not found.'}</Alert>
      </Box>
    );
  }

  const inLook = new Set(look.item_ids);
  const activeImage = look.images.find((img) => img.id === activeImageId) || null;

  const setItems = async (ids: number[]) => {
    try {
      const res = await looksAPI.updateLook(look.id, { item_ids: ids });
      setLook(res.data);
    } catch (err) {
      toast(errorText(err, 'Could not update the look'), 'error');
    }
  };

  const toggleItem = (id: number) =>
    setItems(inLook.has(id) ? look.item_ids.filter((i) => i !== id) : [...look.item_ids, id]);

  const handleAddPhotos = async (files: File[]) => {
    if (files.length === 0) return;
    setUploading({ done: 0, total: files.length });
    const { look: updated, added, failed } = await uploadPhotos(look.id, files, (done) => setUploading({ done, total: files.length }));
    setUploading(null);
    if (updated) {
      setLook(updated);
      setActiveImageId(updated.images[updated.images.length - 1]?.id || null);
    }
    if (failed) toast(`${failed} photo${failed === 1 ? '' : 's'} failed to upload`, 'error');
    else toast(added ? `Matched ${added} piece${added === 1 ? '' : 's'} from your wardrobe` : 'Photo added — no new wardrobe matches', added ? 'success' : 'info');
  };

  const handleDeleteImage = async (imageId: string) => {
    const ok = await confirm({ title: 'Remove this photo?', message: 'Its detected pieces will be removed too. Pieces already in your recreation stay.', confirmLabel: 'Remove', destructive: true });
    if (!ok) return;
    try {
      const res = await looksAPI.deleteImage(look.id, imageId);
      setLook(res.data);
    } catch (err) {
      toast(errorText(err, 'Could not remove the photo'), 'error');
    }
  };

  const handleRematch = async () => {
    setRematching(true);
    try {
      const res = await looksAPI.rematch(look.id);
      setLook(res.data);
      toast('Matches refreshed against your current wardrobe', 'success');
    } catch (err) {
      toast(errorText(err, 'Could not refresh matches'), 'error');
    } finally {
      setRematching(false);
    }
  };

  const handleEdit = async (form: LookForm) => {
    setEditBusy('Saving…');
    try {
      const res = await looksAPI.updateLook(look.id, formToPayload(form));
      setLook(res.data);
      setEditOpen(false);
    } catch (err) {
      toast(errorText(err, 'Could not save changes'), 'error');
    } finally {
      setEditBusy(null);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({ title: 'Delete this look?', message: 'Its photos will be deleted. Outfits you saved from it are kept.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await looksAPI.deleteLook(look.id);
      toast('Look deleted', 'success');
      navigate('/look-book');
    } catch (err) {
      toast(errorText(err, 'Could not delete the look'), 'error');
    }
  };

  const handleSaveOutfit = async () => {
    setSavingOutfit(true);
    try {
      const res = await looksAPI.saveOutfit(look.id, { name: outfitName.trim() || undefined, item_ids: look.item_ids });
      setLook(res.data);
      setSaveOpen(false);
      toast('Outfit saved to this look', 'success');
    } catch (err) {
      toast(errorText(err, 'Could not save the outfit'), 'error');
    } finally {
      setSavingOutfit(false);
    }
  };

  const handleDetach = async (outfitId: number) => {
    try {
      const res = await looksAPI.detachOutfit(look.id, outfitId);
      setLook(res.data);
      toast('Outfit removed from this look (still in Outfits)', 'info');
    } catch (err) {
      toast(errorText(err, 'Could not remove the outfit'), 'error');
    }
  };

  const meta = [look.occasion, look.season].filter(Boolean) as string[];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/look-book')} sx={{ mb: 1 }}>Look Book</Button>

      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{look.name}</Typography>
          {look.description && <Typography color="text.secondary" sx={{ mt: 0.5 }}>{look.description}</Typography>}
          {(meta.length > 0 || look.tags.length > 0) && (
            <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
              {meta.map((m) => <Chip key={m} label={m} size="small" color="primary" variant="outlined" sx={{ textTransform: 'capitalize' }} />)}
              {look.tags.map((t) => <Chip key={t} label={`#${t}`} size="small" />)}
            </Stack>
          )}
          {look.source && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, wordBreak: 'break-all' }}>
              Source:{' '}
              {/^https?:\/\//i.test(look.source)
                ? <a href={look.source} target="_blank" rel="noopener noreferrer">{look.source}</a>
                : look.source}
            </Typography>
          )}
          {look.notes && <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{look.notes}</Typography>}
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Tooltip title="Edit details"><IconButton onClick={() => setEditOpen(true)}><EditIcon /></IconButton></Tooltip>
          <Tooltip title="Delete look"><IconButton onClick={handleDelete}><DeleteOutlineIcon /></IconButton></Tooltip>
        </Stack>
      </Stack>

      {/* Photos */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3 }}>
        <SectionHeader
          title="The look"
          subtitle="Each photo is analyzed to find the pieces and match them to your wardrobe."
          action={
            <Stack direction="row" spacing={1}>
              {look.images.length > 0 && (
                <Button size="small" startIcon={rematching ? <CircularProgress size={14} /> : <RefreshIcon />} onClick={handleRematch} disabled={rematching || !!uploading}>
                  Re-match
                </Button>
              )}
              <Button size="small" variant="contained" startIcon={<AddPhotoAlternateIcon />} onClick={() => fileRef.current?.click()} disabled={!!uploading}>
                Add photos
              </Button>
            </Stack>
          }
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            e.target.value = '';
            handleAddPhotos(files);
          }}
        />
        {uploading && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Analyzing photo {Math.min(uploading.done + 1, uploading.total)} of {uploading.total}…
            </Typography>
            <LinearProgress variant="determinate" value={(uploading.done / uploading.total) * 100} />
          </Box>
        )}
        {look.images.length === 0 ? (
          <PhotoDropzone disabled={!!uploading} onFiles={handleAddPhotos} label="Drag & drop photos of the look to find matching pieces" />
        ) : (
          <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
            {look.images.map((img) => {
              const active = img.id === activeImageId;
              return (
                <Box key={img.id} sx={{ position: 'relative', flex: '0 0 auto' }}>
                  <Box
                    component="img"
                    src={img.url}
                    alt="Look"
                    onClick={() => setActiveImageId(img.id)}
                    sx={{
                      width: { xs: 120, md: 150 }, height: { xs: 160, md: 200 }, objectFit: 'cover', borderRadius: 1.5, cursor: 'pointer',
                      outline: '3px solid', outlineColor: active ? 'primary.main' : 'transparent', outlineOffset: 2,
                    }}
                  />
                  <IconButton
                    size="small"
                    aria-label="Remove photo"
                    onClick={() => handleDeleteImage(img.id)}
                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', p: 0.25, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <Chip
                    size="small"
                    label={`${img.pieces.length} piece${img.pieces.length === 1 ? '' : 's'}`}
                    sx={{ position: 'absolute', bottom: 6, left: 6, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', height: 20, fontSize: '0.65rem' }}
                  />
                </Box>
              );
            })}
            <PhotoDropzone compact disabled={!!uploading} onFiles={handleAddPhotos} />
          </Stack>
        )}

        {/* Detected pieces for the selected photo */}
        {activeImage && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            {activeImage.pieces.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No clothing was detected in this photo. You can still add pieces manually below.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {activeImage.pieces.map((piece, idx) => (
                  <Box key={`${activeImage.id}-${idx}`}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{piece.label}</Typography>
                      {piece.category && <Chip size="small" variant="outlined" label={piece.category} sx={{ textTransform: 'capitalize' }} />}
                      {(piece.colors || []).slice(0, 3).map((c) => <Chip key={c} size="small" label={c} sx={{ textTransform: 'capitalize' }} />)}
                    </Stack>
                    {piece.candidates.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Nothing similar in your wardrobe yet.</Typography>
                    ) : (
                      <Stack direction="row" spacing={1.25} sx={{ overflowX: 'auto', pb: 0.5 }}>
                        {piece.candidates.map((c, n) => {
                          const on = inLook.has(c.id);
                          return (
                            <Card
                              key={c.id}
                              variant="outlined"
                              sx={{ width: 128, flex: '0 0 auto', position: 'relative', outline: '2px solid', outlineColor: on ? 'primary.main' : 'transparent' }}
                            >
                              <CardActionArea onClick={() => toggleItem(c.id)} aria-pressed={on}>
                                <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, bgcolor: '#fff', borderRadius: '50%', display: 'flex' }}>
                                  {on ? <CheckCircleIcon color="primary" /> : <AddCircleOutlineIcon color="action" />}
                                </Box>
                                {n === 0 && (
                                  <Chip size="small" label="Best match" color="secondary" sx={{ position: 'absolute', top: 6, left: 6, zIndex: 1, height: 18, fontSize: '0.6rem' }} />
                                )}
                                <CardMedia component="img" image={c.image_url} alt={c.name} sx={{ height: 110, objectFit: 'cover', objectPosition: 'center top', bgcolor: '#f5f5f5' }} />
                                <Box sx={{ px: 1, py: 0.75 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }} noWrap>{c.name}</Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', textTransform: 'capitalize' }}>
                                    {c.subcategory || c.category}
                                  </Typography>
                                </Box>
                              </CardActionArea>
                            </Card>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Paper>

      {/* Recreation */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3 }}>
        <SectionHeader
          title="Your version"
          subtitle="Pieces you own to recreate this look. Tap matches above or add pieces manually."
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setPickerOpen(true)}>Add pieces</Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={look.item_ids.length === 0}
                onClick={() => { setOutfitName(''); setSaveOpen(true); }}
              >
                Save as outfit
              </Button>
            </Stack>
          }
        />
        {look.items.length === 0 ? (
          <Typography color="text.secondary" variant="body2">No pieces yet.</Typography>
        ) : (
          <Grid container spacing={1.5}>
            {look.items.map((item) => (
              <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={item.id}>
                <ItemTile item={item} onRemove={() => toggleItem(item.id)} />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Saved outfits */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
        <SectionHeader title="Saved outfits" subtitle="Outfits you've saved from this look. They also appear in Outfits." />
        {look.outfits.length === 0 ? (
          <Typography color="text.secondary" variant="body2">Save “Your version” as an outfit to keep it here. You can save several takes.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {look.outfits.map((o) => (
              <Card key={o.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 600 }} noWrap>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.items.length} piece{o.items.length === 1 ? '' : 's'}</Typography>
                  </Box>
                  <AvatarGroup max={6} sx={{ '& .MuiAvatar-root': { width: 40, height: 40 } }}>
                    {o.items.map((it) => <Avatar key={it.id} src={it.image_url} alt={it.name} variant="rounded" />)}
                  </AvatarGroup>
                  <Tooltip title="Use these pieces as your version">
                    <IconButton size="small" onClick={() => setItems(o.items.map((it) => it.id))}><RefreshIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Remove from this look">
                    <IconButton size="small" onClick={() => handleDetach(o.id)}><LinkOffIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Paper>

      <LookFormDialog
        open={editOpen}
        title="Edit look"
        initial={editInitial}
        withPhotos={false}
        describeS3Key={look.images[0]?.s3_key || null}
        busyLabel={editBusy}
        onClose={() => setEditOpen(false)}
        onSubmit={(form) => handleEdit(form)}
      />

      <ItemPickerDialog
        open={pickerOpen}
        selectedIds={look.item_ids}
        onClose={() => setPickerOpen(false)}
        onSave={(ids) => { setPickerOpen(false); setItems(ids); }}
      />

      <Dialog open={saveOpen} onClose={() => !savingOutfit && setSaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save as outfit</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Outfit name"
            placeholder={`${look.name} — Outfit ${look.outfits.length + 1}`}
            value={outfitName}
            onChange={(e) => setOutfitName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Saves the {look.item_ids.length} piece{look.item_ids.length === 1 ? '' : 's'} in “Your version”.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)} disabled={savingOutfit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveOutfit} disabled={savingOutfit}>
            {savingOutfit ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Look list                                                           */
/* ------------------------------------------------------------------ */

const LookListView: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useUI();
  const [looks, setLooks] = useState<LookSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  // Dropping photos anywhere on the page starts a new look with them attached
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => {
      if (accepted.length === 0 || createOpen) return;
      setDroppedFiles(accepted);
      setCreateOpen(true);
    },
    accept: IMAGE_ACCEPT,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  const openCreate = () => {
    setDroppedFiles([]);
    setCreateOpen(true);
  };

  useEffect(() => {
    looksAPI.getLooks()
      .then((res) => setLooks(res.data?.looks || []))
      .catch(() => toast('Failed to load your looks', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  const handleCreate = async (form: LookForm, files: File[]) => {
    setBusy('Creating look…');
    try {
      const res = await looksAPI.createLook(formToPayload(form));
      const lookId: number = res.data.id;
      if (files.length > 0) {
        const { added, failed } = await uploadPhotos(lookId, files, (done) =>
          setBusy(done < files.length ? `Analyzing photo ${done + 1} of ${files.length}…` : 'Finishing up…'));
        if (failed) toast(`${failed} photo${failed === 1 ? '' : 's'} failed to upload`, 'error');
        else if (added) toast(`Matched ${added} piece${added === 1 ? '' : 's'} from your wardrobe`, 'success');
      }
      setCreateOpen(false);
      navigate(`/look-book/${lookId}`);
    } catch (err) {
      toast(errorText(err, 'Could not create the look'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box
      {...getRootProps()}
      data-local-dropzone
      sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 }, minHeight: '70vh', position: 'relative', outline: 'none' }}
    >
      <input {...getInputProps()} />
      {isDragActive && !createOpen && (
        <Box
          sx={{
            position: 'absolute', inset: 8, zIndex: 2, pointerEvents: 'none', borderRadius: 2,
            border: '2px dashed', borderColor: 'secondary.main', bgcolor: 'rgba(212, 175, 55, 0.08)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 56, color: 'secondary.main', mb: 1 }} />
          <Typography variant="h6">Drop to start a new look</Typography>
        </Box>
      )}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Look Book</Typography>
          <Typography color="text.secondary">
            Save looks you love. We'll find the pieces and match them to what you already own.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ flexShrink: 0 }}>
          New look
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : looks.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, px: 2, textAlign: 'center' }}>
          <AutoStoriesIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 0.5 }}>No looks yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Drop in photos of an outfit you want to recreate — a street-style shot, a lookbook page, a screenshot.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Create your first look</Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {looks.map((lk) => (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={lk.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea onClick={() => navigate(`/look-book/${lk.id}`)} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                  {lk.cover_url ? (
                    <CardMedia component="img" image={lk.cover_url} alt={lk.name} sx={{ height: { xs: 200, md: 260 }, objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ height: { xs: 200, md: 260 }, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover' }}>
                      <AutoStoriesIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    </Box>
                  )}
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 600 }} noWrap>{lk.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {lk.item_count} piece{lk.item_count === 1 ? '' : 's'} · {lk.outfit_count} outfit{lk.outfit_count === 1 ? '' : 's'}
                    </Typography>
                    {lk.tags.length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, overflow: 'hidden' }}>
                        {lk.tags.slice(0, 3).map((t) => <Chip key={t} label={`#${t}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />)}
                      </Stack>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <LookFormDialog
        open={createOpen}
        title="New look"
        initial={emptyForm}
        withPhotos
        initialFiles={droppedFiles}
        busyLabel={busy}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
    </Box>
  );
};

const LookBook: React.FC = () => {
  const { lookId } = useParams<{ lookId: string }>();
  const id = lookId ? Number(lookId) : NaN;
  return Number.isFinite(id) ? <LookDetailView key={id} lookId={id} /> : <LookListView />;
};

export default LookBook;
